import { useState } from "react";
import { useThemedTokens } from "../theme";
import { useIsMobile } from "../hooks/useWindowWidth";

const CONF_STYLE = {
  High:   { bg: "rgba(16,185,129,0.12)",  color: "#10b981", border: "rgba(16,185,129,0.4)" },
  Medium: { bg: "rgba(245,158,11,0.12)",  color: "#f59e0b", border: "rgba(245,158,11,0.4)" },
  Low:    { bg: "rgba(239,68,68,0.12)",   color: "#ef4444", border: "rgba(239,68,68,0.4)"  },
};

const RE_EXTRACT_OPTIONS = [
  { value: "budget",      label: "Budget table only" },
  { value: "eligibility", label: "Eligibility requirements only" },
  { value: "evaluation",  label: "Evaluation criteria only" },
  { value: "deadlines",   label: "Deadlines and dates only" },
];

function FieldRow({ label, fieldKey, value, onChange, isCorrect, quote, confidence }) {
  const TOKENS = useThemedTokens(); // colours follow light/dark mode
  const cs = CONF_STYLE[confidence] || CONF_STYLE.Medium;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11, fontWeight: 600, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.08em", flex: 1, fontFamily: TOKENS.fontUI }}>
          {label}
        </label>
        <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: cs.bg, color: cs.color, border: `1px solid ${cs.border}`, fontFamily: TOKENS.fontUI }}>
          {confidence}
        </span>
        {isCorrect
          ? <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 3, background: "rgba(96,165,250,0.12)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.3)", fontFamily: TOKENS.fontUI }}>User-corrected</span>
          : <span style={{ fontSize: 10, color: TOKENS.textDisabled, fontFamily: TOKENS.fontUI }}>AI-extracted</span>
        }
        {quote && quote !== "Not specified" && (
          <span title={`Source: "${quote}"`} style={{ cursor: "help", fontSize: 11, color: "#60a5fa" }}>📄</span>
        )}
      </div>
      <input
        value={value}
        onChange={e => onChange(fieldKey, e.target.value)}
        style={{
          width: "100%", padding: "8px 10px", borderRadius: 6,
          border: `1px solid ${isCorrect ? "rgba(96,165,250,0.5)" : TOKENS.border}`,
          background: TOKENS.bgElevated, color: TOKENS.textPrimary, fontSize: 13,
          outline: "none", boxSizing: "border-box", fontFamily: TOKENS.fontUI,
          transition: "border-color 0.15s",
        }}
      />
    </div>
  );
}

const joinList = (v) => (Array.isArray(v) ? v.filter(Boolean).join("; ") : v || "");

export default function ExtractionReviewModal({ data, sourceName, onConfirm, onDiscard, onReExtract, onSaveToApps, saving }) {
  const TOKENS = useThemedTokens(); // colours follow light/dark mode
  const d = data || {};
  const isMobile   = useIsMobile();
  const confidence = d._confidence || "Medium";
  const quotes     = d._quotes || {};

  const initial = {
    title:        d.opportunityIdentification?.callTitle        || "",
    funder:       d.opportunityIdentification?.funderName       || "",
    deadline:     d.timeline?.applicationDeadline              || "",
    awardMin:     d.fundingParameters?.awardMin                || "",
    awardMax:     d.fundingParameters?.awardMax                || "",
    opportunityId: d.opportunityIdentification?.opportunityId  || "",
    // Current extraction shape first; the older keys are kept for saved records.
    eligibility:  joinList(d.eligibility?.organizationalEligibility)
                  || d.eligibilityAndCompliance?.eligibilitySummary
                  || joinList(d.eligibilityAndCompliance?.keyEligibilityRequirements) || "",
    geoFocus:     joinList(d.programDescription?.geographicFocus)
                  || joinList(d.geographicAndDemographic?.geographicFocus || d.geographicAndDemographic?.targetCountries) || "",
  };
  const conflicts = Array.isArray(d._conflicts) ? d._conflicts : [];

  const [fields,    setFields]    = useState(initial);
  const [corrected, setCorrected] = useState(new Set());
  const [reFocus,   setReFocus]   = useState("");

  const handleChange = (key, val) => {
    setFields(f => ({ ...f, [key]: val }));
    if (val !== initial[key]) setCorrected(s => new Set(s).add(key));
    else setCorrected(s => { const n = new Set(s); n.delete(key); return n; });
  };

  const docRows = [
    ["File",        sourceName || "—"],
    ["Source type", d._sourceType || "—"],
    ["Extracted",   d._extractedAt ? new Date(d._extractedAt).toLocaleString() : "—"],
    ["Confidence",  confidence],
    ["Corrections", corrected.size > 0 ? `${corrected.size} field${corrected.size > 1 ? "s" : ""} edited` : "None"],
  ];

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center",
      padding: 16,
    }}>
      <div style={{
        background: TOKENS.bgCard, borderRadius: 12, border: `1px solid ${TOKENS.border}`,
        width: "100%", maxWidth: 880, maxHeight: "92vh", overflow: "hidden",
        display: "flex", flexDirection: "column", boxShadow: "0 24px 48px rgba(0,0,0,0.6)",
      }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: TOKENS.textPrimary, fontFamily: TOKENS.fontHeading }}>Review Extraction</div>
            <div style={{ fontSize: 12, color: TOKENS.textMuted, marginTop: 2, fontFamily: TOKENS.fontUI }}>Verify or correct fields. Confirm to view, or save to your Applications list.</div>
          </div>
          <button onClick={onDiscard} disabled={saving} style={{ background: "none", border: "none", color: TOKENS.textMuted, cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: 0 }}>

          {/* Left: doc info + re-extract */}
          <div style={{ width: isMobile ? "100%" : "38%", maxHeight: isMobile ? "40vh" : undefined, padding: "20px", borderRight: isMobile ? "none" : `1px solid ${TOKENS.border}`, borderBottom: isMobile ? `1px solid ${TOKENS.border}` : "none", background: TOKENS.bgApp, flexShrink: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 0 }}>

            {d._validationError && (
              <div style={{ padding: "10px 12px", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, color: "#ef4444", fontSize: 12, marginBottom: 12, lineHeight: 1.5, fontFamily: TOKENS.fontUI }}>
                ⚠ Structural errors detected. Review carefully or re-upload.
              </div>
            )}
            {d._sourceType === "ocr" && (
              <div style={{ padding: "10px 12px", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, color: "#f59e0b", fontSize: 12, marginBottom: 12, lineHeight: 1.5, fontFamily: TOKENS.fontUI }}>
                Scanned document (OCR). Accuracy may be lower — double-check all numbers.
              </div>
            )}
            {d._truncationWarning && (
              <div style={{ padding: "10px 12px", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.4)", borderRadius: 8, color: "#f59e0b", fontSize: 12, marginBottom: 12, lineHeight: 1.5, fontFamily: TOKENS.fontUI }}>
                Large document — the middle section was not read. A field marked "Not specified" may be in the part that was skipped.
              </div>
            )}
            {conflicts.length > 0 && (
              <div style={{ padding: "10px 12px", background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.4)", borderRadius: 8, color: "#ef4444", fontSize: 12, marginBottom: 12, lineHeight: 1.5, fontFamily: TOKENS.fontUI }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>⚠ The document contradicts itself</div>
                {conflicts.map((c, i) => (
                  <div key={i} style={{ marginBottom: 6 }}>
                    <div style={{ fontWeight: 600 }}>{c.field}</div>
                    {c.values.map((v, j) => (
                      <div key={j}>• {v.value}{v.locator ? <span style={{ opacity: 0.75 }}> — {v.locator}</span> : null}</div>
                    ))}
                  </div>
                ))}
                <div style={{ opacity: 0.85 }}>Confirm with the funder before relying on either value.</div>
              </div>
            )}

            <div style={{ fontSize: 11, fontWeight: 600, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12, fontFamily: TOKENS.fontUI }}>Document Info</div>
            {docRows.map(([k, v]) => (
              <div key={k} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: TOKENS.textDisabled, fontFamily: TOKENS.fontUI }}>{k}</div>
                <div style={{ fontSize: 13, color: TOKENS.textPrimary, fontFamily: TOKENS.fontUI, marginTop: 2, wordBreak: "break-all" }}>{v}</div>
              </div>
            ))}

            <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${TOKENS.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 8, fontFamily: TOKENS.fontUI }}>Re-extract with focus on…</div>
              <select
                value={reFocus}
                onChange={e => setReFocus(e.target.value)}
                style={{ width: "100%", padding: "7px 10px", background: TOKENS.bgElevated, border: `1px solid ${TOKENS.border}`, borderRadius: 6, color: TOKENS.textPrimary, fontSize: 12, marginBottom: 8, outline: "none", fontFamily: TOKENS.fontUI }}
              >
                <option value="">Select focus…</option>
                {RE_EXTRACT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                onClick={() => reFocus && onReExtract(reFocus)}
                disabled={!reFocus || saving}
                style={{
                  width: "100%", padding: "7px", borderRadius: 6,
                  border: `1px solid ${TOKENS.border}`, background: TOKENS.bgElevated,
                  color: reFocus ? TOKENS.textPrimary : TOKENS.textDisabled,
                  fontSize: 12, cursor: (reFocus && !saving) ? "pointer" : "not-allowed",
                  fontFamily: TOKENS.fontUI,
                }}
              >
                Re-extract
              </button>
            </div>
          </div>

          {/* Right: editable fields */}
          <div style={{ flex: 1, padding: "20px", overflowY: "auto" }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: TOKENS.textMuted, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, fontFamily: TOKENS.fontUI }}>Extracted Fields</div>
            <FieldRow label="Grant Title"      fieldKey="title"         value={fields.title}         onChange={handleChange} isCorrect={corrected.has("title")}         quote={null}           confidence={confidence} />
            <FieldRow label="Funder"           fieldKey="funder"        value={fields.funder}        onChange={handleChange} isCorrect={corrected.has("funder")}        quote={null}           confidence={confidence} />
            <FieldRow label="Deadline"         fieldKey="deadline"      value={fields.deadline}      onChange={handleChange} isCorrect={corrected.has("deadline")}      quote={quotes.deadline} confidence={confidence} />
            <FieldRow label="Award Min"        fieldKey="awardMin"      value={fields.awardMin}      onChange={handleChange} isCorrect={corrected.has("awardMin")}      quote={quotes.budget}  confidence={confidence} />
            <FieldRow label="Award Max"        fieldKey="awardMax"      value={fields.awardMax}      onChange={handleChange} isCorrect={corrected.has("awardMax")}      quote={quotes.budget}  confidence={confidence} />
            <FieldRow label="Opportunity ID"   fieldKey="opportunityId" value={fields.opportunityId} onChange={handleChange} isCorrect={corrected.has("opportunityId")} quote={null}           confidence={confidence} />
            <FieldRow label="Eligibility"      fieldKey="eligibility"   value={fields.eligibility}   onChange={handleChange} isCorrect={corrected.has("eligibility")}   quote={null}           confidence={confidence} />
            <FieldRow label="Geographic Focus" fieldKey="geoFocus"      value={fields.geoFocus}      onChange={handleChange} isCorrect={corrected.has("geoFocus")}      quote={null}           confidence={confidence} />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "14px 20px", borderTop: `1px solid ${TOKENS.border}`,
          display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap",
          background: TOKENS.bgApp, flexShrink: 0,
        }}>
          <button
            onClick={onDiscard}
            disabled={saving}
            style={{ padding: "8px 18px", borderRadius: 6, border: `1px solid ${TOKENS.border}`, background: "none", color: TOKENS.textMuted, fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontFamily: TOKENS.fontUI }}
          >
            Discard
          </button>
          <button
            onClick={() => onConfirm(fields, [...corrected])}
            disabled={saving}
            style={{ padding: "8px 18px", borderRadius: 6, border: `1px solid ${TOKENS.border}`, background: "none", color: TOKENS.textPrimary, fontSize: 13, cursor: saving ? "not-allowed" : "pointer", fontFamily: TOKENS.fontUI }}
          >
            Confirm (view only)
          </button>
          <button
            onClick={() => onSaveToApps(fields, [...corrected])}
            disabled={saving}
            style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: TOKENS.gold, color: "#0A1628", fontSize: 13, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1, fontFamily: TOKENS.fontUI }}
          >
            {saving ? "Saving…" : "Save to Applications"}
          </button>
        </div>
      </div>
    </div>
  );
}
